/**
 * A conversa aberta do Assistente: carrega os turnos salvos e pergunta.
 * `conversaId` null = conversa nova; o servidor cria a conversa na primeira
 * resposta (`POST assistente` sem `conversa_id`).
 *
 * - O contexto mora no servidor: o histórico que vai ao Gemini sai do banco,
 *   então retomar uma conversa antiga continua de onde parou.
 * - A pergunta aparece na hora (turno "local"); a resposta troca o local
 *   pelos dois turnos gravados. Falha não grava nada no servidor: a pergunta
 *   fica na tela com "Tentar de novo".
 * - Resposta que chega depois de trocar de conversa só atualiza a lista
 *   (`aoSalvar`); quem voltar à conversa lê do servidor.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { lerConversaDoAssistente, perguntarAoAssistente } from "../../../services/centralProjetos";

const VAZIA = Object.freeze({ id: null, titulo: "", turnos: [], carregando: false, erro: null });
const SEM_RESPOSTA = "O assistente não respondeu agora.";

function mesmaConversa(a, b) {
  if (a === null || b === null) return a === b;
  return String(a) === String(b);
}

/**
 * A resposta ainda é da conversa em tela? Conversa salva: o mesmo id. A nova
 * não tem id: vale a mesma "rodada" (clicar em "Nova conversa" de novo
 * começa outra, e a resposta da anterior não cai nela).
 */
function aindaEmTela(emTela, doEnvio) {
  if (!mesmaConversa(emTela.id, doEnvio.id)) return false;
  return doEnvio.id !== null || emTela.rodada === doEnvio.rodada;
}

/** Chave da conversa para o "enviando": a nova não tem id ainda. */
function chaveDe(id) {
  return id === null ? "nova" : String(id);
}

/** O id vem da rota como texto ("12"); ao servidor vai o número. */
function idNumerico(id) {
  return /^\d+$/.test(String(id)) ? Number(id) : id;
}

let contadorLocal = 0;

export default function useConversaDoAssistente(conversaId, { aoSalvar, aoCriar } = {}) {
  const { request } = useAuth();
  const [conversa, setConversa] = useState(VAZIA);
  const [enviandoEm, setEnviandoEm] = useState(null);
  const [falha, setFalha] = useState(null);
  const atual = useRef(conversa);
  atual.current = conversa;
  // A conversa em tela agora (a nova tem id null): a resposta atrasada confere com ela.
  const aberta = useRef({ id: conversaId ?? null, rodada: 0 });
  const avisos = useRef({ aoSalvar, aoCriar });
  avisos.current = { aoSalvar, aoCriar };

  useEffect(() => {
    const id = conversaId ?? null;
    aberta.current = { id, rodada: aberta.current.rodada + 1 };
    setFalha(null);
    if (id === null) {
      setConversa(VAZIA);
      return undefined;
    }
    // Acabou de ser criada aqui: os turnos já estão na tela.
    if (mesmaConversa(atual.current.id, id) && !atual.current.erro) return undefined;
    let valida = true;
    setConversa({ ...VAZIA, id, carregando: true });
    lerConversaDoAssistente(request, id)
      .then((lida) => {
        if (valida) setConversa({ id: lida.id, titulo: lida.titulo || "", turnos: lida.turnos || [], carregando: false, erro: null });
      })
      .catch((erro) => {
        if (valida) setConversa({ ...VAZIA, id, erro });
      });
    return () => {
      valida = false;
    };
    // O `request` muda de identidade a cada render do AuthContext: a conversa só relê quando muda de id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversaId]);

  const enviar = useCallback(
    async (emTela, local) => {
      const chave = emTela.id;
      setFalha(null);
      setEnviandoEm(chaveDe(chave));
      try {
        const corpo = { pergunta: local.texto };
        if (chave !== null) corpo.conversa_id = idNumerico(chave);
        if (local.citada) corpo.citada_id = local.citada.id;
        const resposta = await perguntarAoAssistente(request, corpo);
        const salva = resposta?.conversa;
        if (salva) avisos.current.aoSalvar?.(salva);
        if (salva && aindaEmTela(aberta.current, emTela)) {
          setConversa((c) => ({
            ...c,
            id: salva.id,
            titulo: salva.titulo || c.titulo,
            turnos: [...c.turnos.filter((t) => t.id !== local.id), ...(resposta.turnos || [])],
          }));
          if (chave === null) {
            aberta.current = { id: salva.id, rodada: aberta.current.rodada };
            avisos.current.aoCriar?.(salva);
          }
        }
        return resposta;
      } catch (erro) {
        if (aindaEmTela(aberta.current, emTela)) setFalha({ emTela, local, mensagem: erro?.message || SEM_RESPOSTA });
        return null;
      } finally {
        setEnviandoEm((em) => (em === chaveDe(chave) ? null : em));
      }
    },
    [request],
  );

  /** `citada`: o turno do assistente que a pergunta responde (`{id, texto}`), ou null. */
  const perguntar = useCallback(
    (texto, citada) => {
      contadorLocal += 1;
      const local = {
        id: `local-${contadorLocal}`,
        papel: "pessoa",
        texto,
        citada: citada ? { id: citada.id, papel: "assistente", texto: citada.texto } : null,
        em: new Date().toISOString(),
        local: true,
      };
      setConversa((c) => ({ ...c, turnos: [...c.turnos, local] }));
      return enviar(aberta.current, local);
    },
    [enviar],
  );

  const tentarDeNovo = useCallback(() => (falha ? enviar(falha.emTela, falha.local) : Promise.resolve(null)), [enviar, falha]);

  /** Conversa nova de novo (já estando na nova: a rota não muda, então o estado limpa aqui). */
  const recomecar = useCallback(() => {
    if (aberta.current.id !== null) return;
    aberta.current = { id: null, rodada: aberta.current.rodada + 1 };
    setConversa(VAZIA);
    setFalha(null);
  }, []);

  return {
    conversa,
    enviando: enviandoEm !== null && enviandoEm === chaveDe(conversaId ?? null),
    falha,
    perguntar,
    tentarDeNovo,
    recomecar,
  };
}
